import * as Yup from 'yup';
import { requiredFieldMessage } from '../schemas';
import { requiresStateAndPostalCode, isValidStateForCountry, validatePostalCodeFormat } from '@agentnet/utils';

export const certificationFormSchema = Yup.object().shape({
  firstName: Yup.string().max(40, 'Maximum of 40 characters').required(requiredFieldMessage('First name')),
  lastName: Yup.string().max(40, 'Maximum of 40 characters').required(requiredFieldMessage('Last name')),
  email: Yup.string()
    .trim()
    .email('Invalid Email, @ and . characters required.')
    .max(100, 'Maximum of 100 characters.')
    .required(requiredFieldMessage('Email')),
  confirmEmail: Yup.string()
    .trim()
    .email('Invalid Email, @ and . characters required.')
    .oneOf([Yup.ref('email'), null], 'Emails must match')
    .required(requiredFieldMessage('Confirm email')),
  certification: Yup.boolean().oneOf([true], 'Certification must be checked'),
  officeName: Yup.string().max(150, 'Maximum of 150 characters').required(requiredFieldMessage('Office name')),
  firmTaxId: Yup.string()
    .matches(/^\d{2}-\d{7}$/, 'Firm Tax ID must be in format XX-XXXXXXX')
    .required(requiredFieldMessage('Firm Tax ID')),
  officeAddress1: Yup.string().max(100, 'Maximum of 100 characters').required(requiredFieldMessage('Address 1')),
  officeAddress2: Yup.string().max(100, 'Maximum of 100 characters'),
  officeCity: Yup.string().max(50, 'Maximum of 50 characters').required(requiredFieldMessage('City')),
  officeCountry: Yup.string()
    .max(100, 'Maximum of 100 characters')
    .required(requiredFieldMessage('Country/Jurisdiction')),
  officeState: Yup.string().when('officeCountry', {
    is: (country: string) => requiresStateAndPostalCode(country),
    then: (schema) =>
      schema
        .required(requiredFieldMessage('State'))
        .test('valid-state-for-country', 'Invalid state for the selected country', function (value) {
          const { officeCountry } = this.parent;
          return isValidStateForCountry(value, officeCountry);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  officeZip: Yup.string().when('officeCountry', {
    is: (country: string) => requiresStateAndPostalCode(country),
    then: (schema) =>
      schema.required(requiredFieldMessage('Zip')).test('valid-postal-code', function (value) {
        const { officeCountry } = this.parent;
        const error = validatePostalCodeFormat(value, officeCountry);
        return error ? this.createError({ message: error }) : true;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  officePhone: Yup.string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be in format XXX-XXX-XXXX')
    .required(requiredFieldMessage('Phone')),
  transmitterControlCode: Yup.string()
    .test('alphanumeric', 'Transmitter Control Code must contain only alphanumeric characters', (value) => {
      if (!value || value.trim() === '') return true;
      return /^[a-zA-Z0-9]+$/.test(value.trim());
    })
    .transform((value) => (value ? value.trim() : value)),
  sdtmUserId: Yup.string()
    .test('alphanumeric', 'SDTM User ID must contain only alphanumeric characters', (value) => {
      if (!value || value.trim() === '') return true;
      return /^[a-zA-Z0-9]+$/.test(value.trim());
    })
    .transform((value) => (value ? value.trim() : value)),
});
